import React, { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';

import { useNotification } from 'config/Notification.jsx';

import AuditTrialPage from '../AuditTrialPage.jsx';

import LoadingOverlay from 'components/LoadingOverlay.jsx';

import '../../styles/cap.css';

import { fetchCompanySelectedStandards as fetchCompanySelectedStandardsService, 
  fetchHistoricalAssessmentReportsquery as fetchHistoricalAssessmentReportsqueryService
} from 'services/modules/cap/standardservice';

import {
  Box,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  tabsClasses,
  Tooltip
} from '@mui/material';

import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

const HistoricalAssessmentPage = () => {
  
  // State variables
  const [assessmentId, setAssessmentId] = useState('');
  const [assessmentsList, setAssessmentsList] = useState([]);
  const [auditTrailPopup, setAuditTrailPopup] = useState(false);
  const [count,setCount]=useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [value, setValue] = useState(0);

  // Redux selector
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);

  // React router
  const navigate = useNavigate();

  // Notification
  const {openNotification} = useNotification();

  useEffect(() => {
    if (selectedCompanyContainer?.companyid !== null) {
      getCompanySelectedStandards();
      setValue(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [selectedCompanyContainer]);

  useEffect(() => {
    getPendingAssessments(selectedStandards[value - 1]?.id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [value, page, rowsPerPage]);

  // Handles page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handles change in no of rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Handles change in selected standards
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  
  // Fetches company selected standards based on company id
  const getCompanySelectedStandards = async () => {
    try {
      setSelectedStandards([]);
      let params = { companyid: selectedCompanyContainer.companyid };
      const response = await fetchCompanySelectedStandardsService({  params });
      if (response !== '' && response.data !== undefined) {
        setSelectedStandards(response.data);
        getPendingAssessments(null);
      } else {
        setAssessmentsList([]);
      }
    } catch (error) {
      openNotification('Error while fetching standards','error');
    }
  };

  // Fetches assessments whose status are part of status query
  const getPendingAssessments = async (standard) => {
    setLoading(true);
    setAssessmentsList([]);
    let params = { company: selectedCompanyContainer.companyid, page: page + 1, limit: rowsPerPage };
    if (standard !== null ){
      params.standard = standard;
    }
    try {
      const assessmentsResponse = await fetchHistoricalAssessmentReportsqueryService({  params });
      if (assessmentsResponse !== null) {
        setAssessmentsList(assessmentsResponse?.assessmentReports);
        setCount(assessmentsResponse?.totalRecords);
      }
    } catch (error) {
      openNotification('Error while fetching the assessment details','error');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div>
      <div className="row py-1">
        <p>
          <img className="px-1 icons-style" src="../assets/icons/003-clipboard.svg" alt="View" />
          <b className="px-1">Assessments</b>
          <img className="px-1 icons-style" src="../assets/icons/right-layer.svg" alt="View" />
          History
        </p>
      </div>
      {loading && <LoadingOverlay />}
      <hr />
      <div>
        {selectedStandards && selectedStandards.length > 0 && (
          <Box className="cap-tabs-section"
            sx={{
              flexGrow: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Tabs className="cap-tabs"
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons
              sx={{
                [`& .${tabsClasses.scrollButtons}`]: {
                  '&.Mui-disabled': { opacity: 0.3 },
                },
              }}
            >
              <Tab key={0} label="All" />
              {selectedStandards.map((tab, index) => (
                <Tab key={index + 1} label={tab.name} />
              ))}
            </Tabs>
          </Box>
        )}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow >
                  <TableCell >Assessment id</TableCell>
                  <TableCell >User</TableCell>
                  <TableCell>Standard</TableCell>
                  <TableCell>Assigned date</TableCell>
                  <TableCell>Due date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assessmentsList?.length > 0 ? (
                  assessmentsList.map((item) => (
                    <React.Fragment key={item.id}>
                      <TableRow>
                        <TableCell className="text-blue text-bold" 
                          onClick={() => navigate('/cap/completereportdashboard', { state: { assessmentid: item.assessment_id, standard: item.standardname, status: item.status } })}>
                          {item.assessment_id}
                        </TableCell>
                        <TableCell>{item.username}</TableCell>
                        <TableCell>{item.standardname}</TableCell>
                        <TableCell>{new Date(item.created_date).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(item.due_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {item.status}
                          <Tooltip title="View Mitigation" placement="top">
                            <img src="../assets/icons/right-layer.svg" alt="arrow" className='px-2'
                             onClick={() => navigate('/cap/mitigationreview', { state: { assessmentid: item.assessment_id, standard: item.standardname, status: item.status } })} 
                            />
                          </Tooltip>
                          <Tooltip title="View Audit Trail" placement="top">
                            <InfoIcon color="primary" fontSize="medium" 
                            onClick={() => {
                              setAssessmentId(item.assessment_id);
                              setAuditTrailPopup(!auditTrailPopup);
                            }} 
                          />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <WarningIcon color="black" fontSize="small" />
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 7, 10, 15, 20, 25, 50]}
            component="div"
            count={count || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </div>
      <div>
        {auditTrailPopup && (
          <AuditTrialPage toggleAuditTrial={()=>setAuditTrailPopup(!auditTrailPopup)} assessmentId={assessmentId} />
        )}
      </div>
    </div>
  );
}

export default HistoricalAssessmentPage;
